import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Avatar, Tooltip } from "@mui/material";
import { useSelector } from "react-redux";
import type { RootState } from "../../store";
import type { User } from "../../types/user";
import InitialsAvatar from "./InitialsAvatar";

export type UserAvatarUser = Pick<User, "_id"> &
  Partial<Pick<User, "name" | "username" | "profilePictureUrl">>;

interface UserAvatarProps {
  user: UserAvatarUser | null | undefined;
  size?: number;
  tooltipText?: string;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ user, size = 32, tooltipText }) => {
  const displayName = user?.name || user?.username || user?._id || "User";
  const currentUser = useSelector((state: RootState) => state.user.user);
  const users = useSelector((state: RootState) => state.user.users);

  const profilePictureUrl = useMemo(() => {
    if (user?.profilePictureUrl) {
      return user.profilePictureUrl;
    }
    if (!user?._id) {
      return null;
    }
    if (currentUser?._id === user._id && currentUser.profilePictureUrl) {
      return currentUser.profilePictureUrl;
    }
    const fromList = users.find((candidate) => candidate._id === user._id);
    return fromList?.profilePictureUrl ?? null;
  }, [user?._id, user?.profilePictureUrl, currentUser, users]);

  const [imageFailed, setImageFailed] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset fallback avatar when resolved picture URL changes
  useEffect(() => {
    setImageFailed(false);
  }, [profilePictureUrl]);

  if (profilePictureUrl && !imageFailed) {
    const avatar = (
      <Avatar
        src={profilePictureUrl}
        alt={displayName}
        onError={() => setImageFailed(true)}
        sx={{
          width: size,
          height: size,
          "& img": {
            objectFit: "cover",
          },
        }}
      />
    );

    return <Tooltip title={tooltipText || displayName}>{avatar}</Tooltip>;
  }

  return <InitialsAvatar name={displayName} size={size} tooltipText={tooltipText} />;
};

export default UserAvatar;
